import AuthService from '../../utilities/services/auth.service'

import { classifyMedia, fileNameOf, formatReleaseDate } from './whatsNewLogic'

import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'

const WhereIcon = ({ where }) => {
  const common = {
    width: 18,
    height: 18,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
  }
  if (where === 'Packages') {
    return (
      <svg {...common}>
        <path d="M4 8h16v11H4z" />
        <path d="M8 8V6a4 4 0 0 1 8 0v2" />
      </svg>
    )
  }
  if (where === 'Orders') {
    return (
      <svg {...common}>
        <path d="M7 4h10v16H7z" />
        <path d="M10 8h4M10 12h4M10 16h2" />
      </svg>
    )
  }
  if (where === 'Deposits') {
    return (
      <svg {...common}>
        <rect x="3" y="6" width="18" height="12" rx="2" />
        <path d="M3 10h18" />
      </svg>
    )
  }
  if (where === 'Reporting') {
    return (
      <svg {...common}>
        <path d="M5 17c2-4 3-4 5 0s3 4 5 0 3-4 4 0" />
        <path d="M5 12c2-4 3-4 5 0s3 4 5 0 3-4 4 0" />
      </svg>
    )
  }
  if (where === 'Marketing') {
    return (
      <svg {...common}>
        <path d="M4 10v4l10 4V6L4 10z" />
        <path d="M14 9.5a4 4 0 0 1 0 5" />
      </svg>
    )
  }
  return (
    <svg {...common}>
      <path d="M13 3 5 14h7l-1 7 8-11h-7l1-7z" />
    </svg>
  )
}

const kindPillClass = (kind) =>
  kind === 'improved'
    ? 'whats-new-pill whats-new-pill--improved'
    : 'whats-new-pill'

const sourceOf = (feature, filePath) =>
  feature?.previewSources?.[filePath] || filePath

const audienceLine = (audiences) => {
  const names = (audiences || []).filter(Boolean)
  if (names.length <= 1) return names[0] || ''
  if (names.length === 2) {
    return `${names[0]} and ${names[1].toLowerCase()}`
  }
  const last = names[names.length - 1].toLowerCase()
  return `${names.slice(0, -1).join(', ')}, and ${last}`
}

const Recording = ({ feature, imageIndex, onImageIndex }) => {
  const media = classifyMedia(feature?.mediaPaths)
  if (media.video) {
    return (
      <div className="whats-new-recording">
        <video
          className="whats-new-recording__video"
          src={sourceOf(feature, media.video)}
          controls
          playsInline
        />
        <span className="whats-new-recording__name">
          {fileNameOf(media.video)}
        </span>
      </div>
    )
  }
  if (media.images.length) {
    const index = Math.min(imageIndex || 0, media.images.length - 1)
    const current = media.images[index]
    return (
      <div className="whats-new-recording">
        <img
          className="whats-new-recording__image"
          src={sourceOf(feature, current)}
          alt=""
        />
        <span className="whats-new-recording__name">
          {fileNameOf(current).toUpperCase()}
        </span>
        {media.images.length > 1 && (
          <div className="whats-new-recording__pages">
            {media.images.map((image, page) => (
              <button
                key={image}
                type="button"
                className={
                  page === index
                    ? 'whats-new-recording__page whats-new-recording__page--active'
                    : 'whats-new-recording__page'
                }
                onClick={() => onImageIndex?.(page)}
              >
                {page + 1}
              </button>
            ))}
          </div>
        )}
      </div>
    )
  }
  return null
}

const FeatureBody = ({ feature, eyebrow, showGuide }) => {
  const media = classifyMedia(feature.mediaPaths)
  return (
    <div className="whats-new-step">
      <p className="whats-new-step__eyebrow">{eyebrow}</p>
      <h2 className="whats-new-step__title">{feature.title}</h2>
      <p className="whats-new-step__summary">{feature.summary}</p>
      <div className="whats-new-step__facts">
        <div>
          <span>Where</span>
          <strong>{feature.where}</strong>
        </div>
        <div>
          <span>Availability</span>
          <strong>{feature.availability}</strong>
        </div>
        <div>
          <span>Who can use it</span>
          <strong>{(feature.audiences || []).join(', ')}</strong>
        </div>
      </div>
      <ol className="whats-new-step__steps">
        {(feature.steps || []).map((step) => (
          <li key={step}>{step}</li>
        ))}
      </ol>
      {showGuide &&
        media.pdfs.map((pdf) => (
          <a
            key={pdf}
            className="whats-new-step__guide whats-new-card__media"
            href={sourceOf(feature, pdf)}
            target="_blank"
            rel="noreferrer"
          >
            Read guide · {fileNameOf(pdf)}
          </a>
        ))}
    </div>
  )
}

const FeedCard = ({ feature, onWatch, onLook, onAction, onEdit }) => {
  const media = classifyMedia(feature.mediaPaths)
  const canEdit = AuthService.canManageWhatsNew()
  return (
    <article className="whats-new-card">
      <div className="whats-new-card__icon">
        <WhereIcon where={feature.where} />
      </div>
      <div className="whats-new-card__body">
        <div className="whats-new-card__title-row">
          <h3>{feature.title}</h3>
          <div className="whats-new-card__marks">
            <span className={kindPillClass(feature.kind)}>
              {feature.kind === 'improved' ? 'Improved' : 'New'}
            </span>
            {canEdit && onEdit && (
              <Button
                variant="link"
                className="whats-new-card__edit"
                onClick={onEdit}
              >
                Edit
              </Button>
            )}
          </div>
        </div>
        <p className="whats-new-card__summary">{feature.summary}</p>
        {media.video && (
          <Button
            variant="link"
            className="whats-new-card__link whats-new-card__media"
            onClick={onWatch}
          >
            Watch demo
          </Button>
        )}
        {!media.video && media.images.length > 0 && (
          <Button
            variant="link"
            className="whats-new-card__link whats-new-card__media"
            onClick={onLook}
          >
            Take closer look
          </Button>
        )}
        {media.pdfs.map((pdf) => (
          <a
            key={pdf}
            className="whats-new-card__link whats-new-card__media"
            href={sourceOf(feature, pdf)}
            target="_blank"
            rel="noreferrer"
          >
            Read guide · {fileNameOf(pdf)}
          </a>
        ))}
        <p className="whats-new-card__meta">
          {[
            feature.where,
            feature.availability,
            audienceLine(feature.audiences),
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
        {feature.actionLabel && (
          <Button
            variant="link"
            className="whats-new-card__link"
            onClick={onAction}
          >
            {feature.actionLabel} →
          </Button>
        )}
      </div>
    </article>
  )
}

const TourStep = ({
  feature,
  mode,
  stepLabel,
  imageIndex,
  onImageIndex,
  onHide,
  showClose = true,
  onPrimary,
  onAction,
  primaryLabel,
  onBack,
  stepIndex = 0,
  stepCount = 1,
  onStep,
  className,
}) => {
  if (!feature) return null
  const kindLabel = feature.kind === 'improved' ? 'Improved' : 'New'
  const media = classifyMedia(feature.mediaPaths)
  const hasVisual = Boolean(media.video) || media.images.length > 0
  const actionLabel = String(feature.actionLabel || '').trim()
  return (
    <div
      className={['whats-new-step-layout', className].filter(Boolean).join(' ')}
    >
      <div className="whats-new-modal__bar">
        <span className={kindPillClass(feature.kind)}>{kindLabel}</span>
        {showClose && (
          <Button
            variant="light"
            className="whats-new-modal__close"
            aria-label="Close"
            onClick={onHide}
          >
            ×
          </Button>
        )}
      </div>
      {hasVisual && (
        <div className="whats-new-modal__media">
          <Recording
            feature={feature}
            imageIndex={imageIndex}
            onImageIndex={onImageIndex}
          />
        </div>
      )}
      <Modal.Body>
        <FeatureBody feature={feature} eyebrow={stepLabel} showGuide />
      </Modal.Body>
      <Modal.Footer className="whats-new-modal__foot">
        {mode === 'tour' && stepCount > 1 ? (
          <div className="whats-new-dots">
            {Array.from({ length: stepCount }, (_, index) => (
              <button
                key={index}
                type="button"
                aria-label={`Step ${index + 1}`}
                aria-current={index === stepIndex}
                className={
                  index === stepIndex
                    ? 'whats-new-dots__dot whats-new-dots__dot--active'
                    : 'whats-new-dots__dot'
                }
                onClick={() => onStep?.(index)}
              />
            ))}
          </div>
        ) : (
          <span />
        )}
        <div className="whats-new-modal__actions">
          {mode === 'tour' && stepIndex > 0 && (
            <Button variant="link" onClick={onBack}>
              Back
            </Button>
          )}
          {actionLabel && (
            <Button variant="outline-secondary" onClick={onAction}>
              {actionLabel}
            </Button>
          )}
          {primaryLabel && (
            <Button variant="primary" onClick={onPrimary}>
              {primaryLabel}
            </Button>
          )}
        </div>
      </Modal.Footer>
    </div>
  )
}

const FeatureModal = (props) => (
  <Modal
    show={props.show}
    onHide={props.onHide}
    centered
    className="whats-new-modal"
    contentClassName="whats-new-modal__content"
  >
    <TourStep {...props} />
  </Modal>
)

const ReleaseHeading = ({ release, latest }) => (
  <div className="whats-new-release">
    <span className="whats-new-release__date">
      {formatReleaseDate(release.publishedOn).toUpperCase()}
    </span>
    {latest && <span className="whats-new-pill">Latest</span>}
  </div>
)

export {
  FeatureBody,
  FeatureModal,
  FeedCard,
  Recording,
  ReleaseHeading,
  TourStep,
  WhereIcon,
}
